const API_BASE_URL = "/api";

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string,
    public isNetworkError = false,
    // Field name -> message, from express-validator errors on the backend.
    // Lets a form highlight exactly which input failed instead of just
    // showing this error's generic top-level message.
    public fieldErrors?: Record<string, string>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const handleApiResponse = async (response: Response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");

  if (!response.ok) {
    const errorData = isJson
      ? await response.json()
      : { message: response.statusText };
    throw new ApiError(
      response.status,
      response.statusText,
      errorData.message || "An error occurred",
      false,
      errorData.fieldErrors
    );
  }

  return isJson ? response.json() : response.text();
};

// Without this, a request that hangs server-side (e.g. a stalled Stripe or DB
// call) leaves the UI stuck on "Saving..." forever with no way to recover.
const REQUEST_TIMEOUT_MS = 30000;

const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const config: RequestInit = {
    headers: getAuthHeaders(),
    ...options,
    signal: controller.signal,
  };

  try {
    const response = await fetch(url, config);
    return await handleApiResponse(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError(
        0,
        "Timeout",
        "The server took too long to respond. It may still be processing - check before retrying.",
        true
      );
    }

    // Check if it's a network error (server not running)
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new ApiError(
        0,
        "Network Error",
        "Backend server is not running. Please start the backend server.",
        true
      );
    }

    throw new ApiError(
      0,
      "Network Error",
      "Failed to connect to the server",
      true
    );
  } finally {
    clearTimeout(timeoutId);
  }
};

// API methods
export const api = {
  // GET request
  get: (endpoint: string) => apiRequest(endpoint, { method: "GET" }),

  // POST request
  post: (endpoint: string, data?: any) =>
    apiRequest(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }),

  // PUT request
  put: (endpoint: string, data?: any) =>
    apiRequest(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    }),

  // PATCH request
  patch: (endpoint: string, data?: any) =>
    apiRequest(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    }),

  // DELETE request
  delete: (endpoint: string) => apiRequest(endpoint, { method: "DELETE" }),
};

// User Plans API with offline detection
export const userPlansApi = {
  // Get all plans with optional filters
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    userType?: string;
    disabled?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, value.toString());
          }
        });
      }
      const query = searchParams.toString();
      return await api.get(`/user-plans${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        // Return mock data when backend is offline
        return {
          success: false,
          isOffline: true,
          data: { plans: [], pagination: null },
          message:
            "Backend server is offline. Start the backend to see real data.",
        };
      }
      throw error;
    }
  },

  // Get plan by ID
  getById: (id: string) => api.get(`/user-plans/${id}`),

  // Get plans by user type
  getByUserType: (userType: string, includeDisabled = false) =>
    api.get(
      `/user-plans/user-type/${userType}?includeDisabled=${includeDisabled}`
    ),

  // Get default plans
  getDefaults: () => api.get("/user-plans/public/defaults"),

  // Get statistics with offline handling
  getStatistics: async () => {
    try {
      return await api.get("/user-plans/admin/statistics");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // Create new plan
  create: (planData: any) => api.post("/user-plans", planData),

  // Update existing plan
  update: (id: string, planData: any) => api.put(`/user-plans/${id}`, planData),

  // Delete plan
  delete: (id: string) => api.delete(`/user-plans/${id}`),

  // Toggle plan status
  toggleStatus: (id: string) => api.patch(`/user-plans/${id}/toggle-status`),

  // Set as default plan
  setDefault: (id: string) => api.patch(`/user-plans/${id}/set-default`),

  // Duplicate plan
  duplicate: (id: string, planName: string) =>
    api.post(`/user-plans/${id}/duplicate`, { planName }),
};

// Admin Promotions API
export const promotionsApi = {
  // Get all promotions
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    userType?: string;
    duration?: string;
    isActive?: boolean;
    expired?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const query = searchParams.toString();
    return api.get(`/promotions${query ? `?${query}` : ""}`);
  },

  // Get promotion by ID
  getById: (id: string) => api.get(`/promotions/${id}`),

  // Get promotion by code
  getByCode: (code: string) => api.get(`/promotions/public/code/${code}`),

  // Get active promotions
  getActive: (userType?: string) => {
    const query = userType ? `?userType=${userType}` : "";
    return api.get(`/promotions/public/active${query}`);
  },

  // Get statistics
  getStatistics: () => api.get("/promotions/admin/statistics"),

  // Create promotion
  create: (promotionData: any) => api.post("/promotions", promotionData),

  // Update promotion
  update: (id: string, promotionData: any) =>
    api.put(`/promotions/${id}`, promotionData),

  // Delete promotion
  delete: (id: string) => api.delete(`/promotions/${id}`),

  // Toggle promotion status
  toggleStatus: (id: string) => api.patch(`/promotions/${id}/toggle-status`),

  // Use promotion
  use: (code: string, userId?: string) =>
    api.post(`/promotions/use/${code}`, { userId }),

  // Duplicate promotion
  duplicate: (id: string, coupon_code: string, name: string) =>
    api.post(`/promotions/${id}/duplicate`, { coupon_code, name }),

  // Bulk update promotions
  bulkUpdate: (promotionIds: string[], updates: any) =>
    api.put("/promotions/bulk/update", { promotionIds, updates }),
};

// user related APIs with offline detection
export const usersApi = {
  // user login api
  login: async (params?: {
    phoneNumber: number | string;
    password: string;
  }) => {
    try {
      return await api.post("/auth/login", params);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        // Return mock data when backend is offline
        return {
          success: false,
          isOffline: true,
          data: { plans: [], pagination: null },
          message:
            "Backend server is offline. Start the backend to see real data.",
        };
      }
      throw error;
    }
  },

  // Get statistics with offline handling
  getUsersStatistics: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/auth/user-stats");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // Get roles with offline handling
  getUserRoles: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/roles");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

   // Get users with offline handling
   getUsers: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/users");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // Toggle user disabled status — backend only exposes a toggle, not a direct set
  updateUserStatus: (userId: string, disabled: boolean) =>
    api.patch(`/users/${userId}/toggle-status`, { disabled }),

  // Update user profile fields — backend does a raw $set of the body, so use
  // dot-notation keys (e.g. "profile.firstName") to patch nested fields
  // without clobbering sibling fields in the same subdocument.
  updateUser: (userId: string, data: Record<string, any>) =>
    api.put(`/users/${userId}`, data),

  // Bulk import users from a CSV file. Uses FormData instead of api.post so no
  // Content-Type is set here — the browser fills in multipart/form-data with
  // the correct boundary itself when the body is a FormData instance.
  importUsers: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("authToken");
      return await apiRequest("/users/import", {
        method: "POST",
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },
};

// chat related APIs with offline detection
export const chatRelatedApi = {
  // Get chats with offline handling
  getAllChats: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/chat");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // Get chat by ID
  getById: (id: string) => api.get(`/chat/${id}`),
};

// promotional coupon related APIs with offline detection
export const couponRelatedApi = {
  // Get promotional coupons with offline handling
  getAllCoupons: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/promotions");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // Get chat by ID
  // getById: (id: string) => api.get(`/chat/${id}`),
};

// user queries related APIs with offline detection
export const queriesRelatedApi = {
  // Get user queries with offline handling
  getAllQueries: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/queries");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // send reply
  sendReply: (replyData: any) => api.post(`/queries/${replyData.id}/reply`, replyData),

  // Get chat by ID
  // getById: (id: string) => api.get(`/chat/${id}`),
};

// settings related APIs with offline detection
export const settingsRelatedApi = {
  // Get notification settings with offline handling
  getCoachSettings: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/notifications/defaults/coach");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

  // Get notification settings with offline handling
  getAthleteSettings: async (params?: { page: number; limit: number }) => {
    try {
      return await api.get("/notifications/defaults/athlete");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: null,
          message: "Backend server is offline",
        };
      }
      throw error;
    }
  },

};

// Static data APIs
export const staticDataApi = {
  getSports: async () => {
    try {
      return await api.get("/static/sports");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  getOrganizationTypes: async () => {
    try {
      return await api.get("/static/organization-types");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  getStaticData: async () => {
    try {
      return await api.get("/static/static-data");
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  getStoragePlans: async (params?: { page?: number; limit?: number; search?: string }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.append(key, value.toString());
        });
      }
      const query = searchParams.toString();
      return await api.get(`/static/storage-plans${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  getPackages: async (params?: { page?: number; limit?: number }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.append(key, value.toString());
        });
      }
      const query = searchParams.toString();
      return await api.get(`/packages${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  getPrograms: async (params?: { page?: number; limit?: number }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.append(key, value.toString());
        });
      }
      const query = searchParams.toString();
      return await api.get(`/programs${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  // ── Sports CRUD ──────────────────────────────────────────────────────────
  createSport: (data: { name: string; disabled?: boolean }) =>
    api.post("/static/sports", data),
  updateSport: (id: string, data: { name?: string; disabled?: boolean }) =>
    api.put(`/static/sports/${id}`, data),
  toggleSportStatus: (id: string) => api.patch(`/static/sports/${id}/toggle-status`),
  deleteSport: (id: string) =>
    api.delete(`/static/sports/${id}`),

  // ── Organization Types CRUD ──────────────────────────────────────────────
  createOrganizationType: (data: { organizationType: string; disabled?: boolean }) =>
    api.post("/static/organization-types", data),
  updateOrganizationType: (id: string, data: { organizationType?: string; disabled?: boolean }) =>
    api.put(`/static/organization-types/${id}`, data),
  toggleOrganizationTypeStatus: (id: string) =>
    api.patch(`/static/organization-types/${id}/toggle-status`),
  deleteOrganizationType: (id: string) =>
    api.delete(`/static/organization-types/${id}`),

  // ── Static KV CRUD ───────────────────────────────────────────────────────
  createStaticData: (data: { key: string; value: string }) =>
    api.post("/static/static-data", data),
  updateStaticData: (id: string, data: { key?: string; value?: string }) =>
    api.put(`/static/static-data/${id}`, data),
  toggleStaticDataStatus: (id: string) =>
    api.patch(`/static/static-data/${id}/toggle-status`),
  deleteStaticData: (id: string) =>
    api.delete(`/static/static-data/${id}`),

  // ── Storage Plans CRUD ───────────────────────────────────────────────────
  createStoragePlan: (data: { key: string; label: string; gb: number; price: number }) =>
    api.post("/static/storage-plans", data),
  updateStoragePlan: (id: string, data: { key?: string; label?: string; gb?: number; price?: number; disabled?: boolean }) =>
    api.put(`/static/storage-plans/${id}`, data),
  toggleStoragePlanStatus: (id: string) =>
    api.patch(`/static/storage-plans/${id}/toggle-status`),
  deleteStoragePlan: (id: string) =>
    api.delete(`/static/storage-plans/${id}`),

  // ── Packages CRUD ────────────────────────────────────────────────────────
  createPackage: (data: any) => api.post("/packages", data),
  updatePackage: (id: string, data: any) => api.put(`/packages/${id}`, data),
  togglePackageStatus: (id: string) => api.patch(`/packages/${id}/toggle-status`),
  deletePackage: (id: string) => api.delete(`/packages/${id}`),

  // ── Programs CRUD ────────────────────────────────────────────────────────
  createProgram: (data: any) => api.post("/programs", data),
  updateProgram: (id: string, data: any) => api.put(`/programs/${id}`, data),
  toggleProgramStatus: (id: string) => api.patch(`/programs/${id}/toggle-status`),
  deleteProgram: (id: string) => api.delete(`/programs/${id}`),
};

// organizations related APIs with offline detection
export const organizationsApi = {
  // Get all organizations with offline handling
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    organizationType?: string;
    disabled?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            searchParams.append(key, value.toString());
          }
        });
      }
      const query = searchParams.toString();
      return await api.get(`/organizations${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: { organizations: [], pagination: null },
          message:
            "Backend server is offline. Start the backend to see real data.",
        };
      }
      throw error;
    }
  },

  // Get organization by ID
  getById: (id: string) => api.get(`/organizations/${id}`),

  // Create organization
  create: (data: any) => api.post("/organizations", data),

  // Update organization
  update: (id: string, data: any) => api.put(`/organizations/${id}`, data),

  // Delete organization
  delete: (id: string) => api.delete(`/organizations/${id}`),

  // Toggle organization status
  toggleStatus: (id: string) =>
    api.patch(`/organizations/${id}/toggle-status`),
};

// Coupons API
export const couponsApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isFreeGrant?: boolean;
  }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.append(key, value.toString());
        });
      }
      const query = searchParams.toString();
      return await api.get(`/coupons${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: { coupons: [], pagination: null },
          message: "Backend server is offline. Start the backend to see real data.",
        };
      }
      throw error;
    }
  },

  getById: (id: string) => api.get(`/coupons/${id}`),

  create: (data: any) => api.post("/coupons", data),

  update: (id: string, data: any) => api.put(`/coupons/${id}`, data),

  delete: (id: string) => api.delete(`/coupons/${id}`),

  toggleStatus: (id: string) => api.patch(`/coupons/${id}/toggle-status`),

  getShareLink: (id: string) => api.get(`/coupons/${id}/share-link`),

  // Uploads a coupon thumbnail image, returning its stored URL. Uses FormData
  // instead of api.post so no Content-Type is set here — the browser fills in
  // multipart/form-data with the correct boundary itself.
  uploadThumbnail: async (file: File) => {
    const formData = new FormData();
    formData.append("thumbnail", file);
    const token = localStorage.getItem("authToken");
    return await apiRequest("/coupons/upload-thumbnail", {
      method: "POST",
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
};

// Roles API
export const rolesApi = {
  getAll: async (params?: { search?: string; isActive?: boolean }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.append(key, value.toString());
        });
      }
      const query = searchParams.toString();
      return await api.get(`/roles${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return {
          success: false,
          isOffline: true,
          data: { roles: [] },
          message: "Backend server is offline. Start the backend to see real data.",
        };
      }
      throw error;
    }
  },

  getById: (id: string) => api.get(`/roles/${id}`),

  create: (data: { name: string; description?: string; permissions?: string[] }) =>
    api.post("/roles", data),

  update: (
    id: string,
    data: { name?: string; description?: string; permissions?: string[]; isActive?: boolean }
  ) => api.put(`/roles/${id}`, data),

  toggleStatus: (id: string) => api.patch(`/roles/${id}/toggle-status`),

  delete: (id: string) => api.delete(`/roles/${id}`),
};

// Coaches API
export const coachesApi = {
  // Get all coaches
  getAll: async (params?: { page?: number; limit?: number; search?: string }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) searchParams.append(key, value.toString());
        });
      }
      const query = searchParams.toString();
      return await api.get(`/coaches${query ? `?${query}` : ""}`);
    } catch (error) {
      if (error instanceof ApiError && error.isNetworkError) {
        return { success: false, isOffline: true, data: null, message: "Backend server is offline" };
      }
      throw error;
    }
  },

  // Get coach by ID
  getById: (id: string) => api.get(`/coaches/${id}`),

  // Update coach
  update: (id: string, data: any) => api.put(`/coaches/${id}`, data),

  // Delete coach
  delete: (id: string) => api.delete(`/coaches/${id}`),
};

export { ApiError };
